import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { subjects, type AuthUser } from "@pahcertificar/certmanager-auth-core";
import {
  authClient,
  REDIRECT_URI,
  clearTokens,
  loadTokens,
  saveChallenge,
  saveTokens,
} from "@/lib/auth-client";

type AuthState =
  | { status: "loading"; user: null }
  | { status: "unauthenticated"; user: null }
  | { status: "authenticated"; user: AuthUser };

type AuthContextValue = AuthState & {
  signIn: () => Promise<void>;
  signOut: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading", user: null });

  useEffect(() => {
    let cancelled = false;
    const tokens = loadTokens();
    if (!tokens) {
      setState({ status: "unauthenticated", user: null });
      return;
    }
    (async () => {
      try {
        const verified = await authClient.verify(subjects, tokens.access, {
          refresh: tokens.refresh,
        });
        if (cancelled) return;
        if (verified.err) {
          clearTokens();
          setState({ status: "unauthenticated", user: null });
          return;
        }
        if (verified.tokens) {
          saveTokens({ access: verified.tokens.access, refresh: verified.tokens.refresh });
        }
        setState({
          status: "authenticated",
          user: verified.subject.properties as AuthUser,
        });
      } catch {
        if (cancelled) return;
        clearTokens();
        setState({ status: "unauthenticated", user: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async () => {
    const { challenge, url } = await authClient.authorize(REDIRECT_URI, "code", {
      pkce: true,
      provider: "code",
    });
    saveChallenge(challenge);
    window.location.href = url;
  }, []);

  const signOut = useCallback(() => {
    clearTokens();
    setState({ status: "unauthenticated", user: null });
    window.location.assign("/login");
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, signIn, signOut }),
    [state, signIn, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

/**
 * Convenience hook that asserts the user is authenticated. Use inside routes
 * gated by RequireAuth where a non-null user is guaranteed.
 */
export function useAuthUser(): AuthUser {
  const auth = useAuth();
  if (auth.status !== "authenticated") {
    throw new Error("useAuthUser called from a non-authenticated context");
  }
  return auth.user;
}
